const CMS_BASE = "/_api/public";

// Fallback seed data in case network is unreachable during build or SSR
const FALLBACK_DATA = {
  pages: [
    {
      title: "Home",
      page_key: "home",
      seo_title: "Prajwal Halwai | Freelance Web Developer and Software Developer",
      seo_description: "I build web applications, SaaS platforms, e-commerce platforms, and MVPs for founders and businesses worldwide. Fast to market, built to scale.",
      headline: "Have an idea worth building? Let's turn it into the right product.",
      subheadline: "I help founders and businesses turn ideas into reliable web applications, SaaS products and digital platforms.",
      content: "<p>Whether you’re a founder with an idea or a company with a problem, I help you ship the right thing without wasting time or starting over.</p>",
      featured_image: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/profile.webp"],
      gallery_slug: "home-gallery"
    },
    {
      title: "Work & Case Studies",
      page_key: "work",
      seo_title: "Portfolio - Prajwal Halwai",
      seo_description: "Software architecture and web development case studies by Prajwal Halwai.",
      headline: "Featured Work & Architectural Cases",
      subheadline: "Real-world engineering solutions solving real business problems. From web architecture to complete redesigns.",
      content: "<p>Software engineering that prioritizes scalable foundations, maintainable codebases, and direct business results.</p>",
      featured_image: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-1.webp"],
      gallery_slug: "home-gallery"
    },
    {
      title: "About Prajwal",
      page_key: "about",
      seo_title: "About - Prajwal Halwai",
      seo_description: "Senior software engineering consultant offering direct collaboration, technical architecture, and product mindset based in Odense, Denmark.",
      headline: "Software Engineer & Product Partner",
      subheadline: "Direct collaboration with an experienced software developer - no account managers, no junior handoffs.",
      content: "<p>I help founders and businesses move from initial concept to reliable, scalable digital products.</p>",
      featured_image: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-1.webp"],
      gallery_slug: "about-gallery"
    },
    {
      title: "Contact",
      page_key: "contact",
      seo_title: "Let's Talk | Prajwal Halwai",
      seo_description: "Book a discovery call and tell me about your project. I'll listen, ask the right questions, and tell you if I can help.",
      headline: "Have an idea? Let's talk about it.",
      subheadline: "Not sure where to start? Tell me what’s on your mind, and I’ll tell you if I can help.",
      content: "<p>Everything is delivered by me, directly. No middlemen, no handoffs. Available for founders and businesses worldwide.</p>",
      featured_image: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/profile.webp"],
      gallery_slug: "home-gallery"
    }
  ],
  services: [
    {
      title: "Web Applications & SaaS Platforms",
      service_key: "web-applications-saas",
      short_description: "Scalable digital products, SaaS platforms, and browser-based applications built around real business needs and validated architecture.",
      full_description: "<p>Whether you’re a founder with an idea or an established business launching a new digital platform, I help you architect and develop web applications that are fast to market and built to scale.</p><p>I build clean, high-performance web products with modern architecture, robust backend APIs, and intuitive user interfaces. Every decision is made with scale and maintainability in mind from day one, so you never have to throw away code or start over.</p>",
      icon: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/noun-web-application-6443508-cc3737.png"],
      audience: "Founders, startups, and companies building subscription models, customer portals, or interactive web applications.",
      approach: "<p>We start by clarifying user journeys, critical data flows, and monetization architecture. From there, I build frontend and backend in rapid sprints with continuous visibility, deploying functional increments early.</p>",
      deliverables: "<ul><li>Production-ready web application & database architecture</li><li>User authentication, subscription billing & role-based access</li><li>REST / GraphQL APIs and scalable cloud backend</li><li>Clean, modern responsive frontend</li></ul>",
      cta_text: "Explore Web & SaaS Development",
      cta_link: "/contact",
      seo_title: "Web Applications & SaaS - Prajwal Halwai",
      seo_description: "Custom web applications and scalable SaaS platforms built for founders and businesses worldwide. Built to scale.",
      order: 1,
      gallery_slug: "services-gallery"
    },
    {
      title: "Minimum Viable Products (MVPs)",
      service_key: "mvp-development",
      short_description: "You have an idea and need to validate it fast. I build web-based MVPs that are lean enough to ship quickly and solid enough to grow from – no throwaway code.",
      full_description: "<p>The biggest trap for founders is over-engineering before validation. I help you identify the critical core of your product, prioritize the features that test your key hypothesis, and build a focused initial product in weeks rather than months.</p><p>The Minimum Viable Product is the start, not the finish. It must be rock-solid and structured so you can scale as your product and user base grow.</p>",
      icon: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/noun-minimum-viable-product-286705-cc3737.png"],
      audience: "Founders, entrepreneurs, and innovation leads who want to launch quickly, test real market demand, and conserve budget.",
      approach: "<p>01 - Understand the core problem and testable hypothesis.<br>02 - Strip out distraction features to define the essential MVP scope.<br>03 - Rapid engineering sprint to ship in weeks.<br>04 - Launch and gather real user metrics.</p>",
      deliverables: "<ul><li>Scoped MVP specification & technical roadmap</li><li>Functional core product ready for live users</li><li>Payment / onboarding flow and analytics tracking</li><li>Architectural foundation ready for version 2.0 expansion</li></ul>",
      cta_text: "Build your MVP",
      cta_link: "/contact",
      seo_title: "MVP Development - Prajwal Halwai",
      seo_description: "Fast-to-market Minimum Viable Products for founders and startups. Ship in weeks without throwaway code.",
      order: 2,
      gallery_slug: "services-gallery"
    },
    {
      title: "E-commerce Platforms",
      service_key: "ecommerce-platforms",
      short_description: "Shopify or fully custom – online stores that are fast, conversion-focused, and built to handle growth. From initial setup to complex integrations.",
      full_description: "<p>I help run an e-commerce business myself, from strategy to technical execution. That combination means I bring both engineering depth and an owner’s commercial mindset to your store.</p><p>Whether you need custom Shopify theme development, headless e-commerce, custom product configurators, or complex ERP/warehouse integrations, I build commerce platforms that convert visitors into buyers and scale effortlessly under load.</p>",
      icon: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/noun-ecommerce-8357643-cc3737.png"],
      audience: "Brands, direct-to-consumer businesses, and retailers outgrowing standard templates who need higher conversions, custom workflows, or bespoke features.",
      approach: "<p>Every element is optimized for speed, seamless mobile checkout, and frictionless customer journey, paired with reliable automated inventory and order pipelines.</p>",
      deliverables: "<ul><li>Custom Shopify theme or headless e-commerce build</li><li>Custom product customizers, bundles, and checkout optimizations</li><li>Payment gateway, ERP, CRM, and logistics integrations</li><li>High-speed mobile performance and SEO architecture</li></ul>",
      cta_text: "Scale Your E-commerce",
      cta_link: "/contact",
      seo_title: "E-commerce Platforms - Prajwal Halwai",
      seo_description: "High-performance e-commerce platforms and custom Shopify development built for high conversion and scale.",
      order: 3,
      gallery_slug: "services-gallery"
    },
    {
      title: "Internal Tools & Dashboards",
      service_key: "internal-tools-dashboards",
      short_description: "Software that helps businesses improve workflows, visibility, and internal processes. Custom administrative dashboards and workflow solutions.",
      full_description: "<p>Off-the-shelf software often forces your team into rigid, inefficient processes. Custom internal tools and operational dashboards give your team exactly what they need to execute faster and make data-driven decisions.</p><p>I design and build tailored internal dashboards, CRM extensions, inventory monitors, reporting tools, and workflow automations that unify fragmented systems into one clean, reliable interface.</p>",
      icon: ["https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/noun-7815938-cc3737.png"],
      audience: "Growing businesses, operations teams, and product managers struggling with manual spreadsheets or fragmented data across tools.",
      approach: "<p>We audit existing internal bottlenecks, map out operational data connections, and deliver secure, fast role-based tools that save hours of manual labor weekly.</p>",
      deliverables: "<ul><li>Custom administrative consoles & workflow automation</li><li>Real-time reporting dashboards and data visualization</li><li>Internal API bridges connecting databases, CRMs, and third-party tools</li><li>Secure team permissioning and audit logs</li></ul>",
      cta_text: "Build Internal Tools",
      cta_link: "/contact",
      seo_title: "Internal Tools & Dashboards - Prajwal Halwai",
      seo_description: "Custom internal tools, operational dashboards, and workflow automation to streamline business operations.",
      order: 4,
      gallery_slug: "services-gallery"
    }
  ],
  case_studies: [
    {
      title: "Ongoing work",
      case_key: "ongoing-work",
      category: "Current",
      solution: "<p>At the moment, I split my time between my own AI projects and helping a few customers with their projects. Details on request.</p>"
    },
    {
      title: "Prajaya",
      case_key: "prajaya",
      category: "E-commerce",
      solution: "<p>Prajaya is a Danish brand selling handcrafted singing bowls, statues and other crafts from artisans in Nepal and beyond. I built and maintain their online store, prajaya.dk, on the Shopify platform. Alongside the build, I run their SEO, Google and Meta ads, and social media marketing, learning the growth side of e-commerce hands-on. The store is bilingual, ships to several EU markets, and serves both B2C and B2B customers.</p>"
    },
    {
      title: "Nordic Fintech Week",
      case_key: "nordic-fintech-week",
      category: "Fintech",
      solution: "<p>I built custom WordPress plugins for Copenhagen Fintech's Nordic Fintech Week 2026 website that show the event programme and speakers.</p>"
    },
    {
      title: "Tokerød Plus",
      case_key: "tokeroed-plus",
      category: "Software development agency",
      solution: "<p>I was part of a small team taking customer projects from idea and development through to deployment, including CarMatch and SmartBeboer. I also did technical architecture advisory work for customers such as Energi Fyn.</p><p>The piece I'm proudest of is a custom-built plugin for SmartBeboer that paid out money to users automatically through Danske Bank's secure business-banking web services.</p>"
    },
    {
      title: "Dansk Motor Finans",
      case_key: "dansk-motor-finans",
      category: "Autotech",
      solution: "<p>I extended DMF's online payments to cover more of the ways Danish car dealerships actually pay through the company portal. I also built an internal tool that took over complex manual work for a business-critical team.</p>"
    },
    {
      title: "QuadSAT Cloud",
      case_key: "quadsat-cloud",
      category: "Satellite Communications",
      solution: "<p>QuadSAT's antenna measurement for ground stations ran only as desktop software. I designed and built the first version of its cloud platform: the APIs, user management and data pipeline that connect to the desktop tool and store measurement data centrally. I defined what the platform would be before any of it existed.</p>"
    },
    {
      title: "Grundfos iSOLUTIONS Platform",
      case_key: "grundfos-isolutions-platform",
      category: "Water Technology",
      solution: "<p>I was on the team that took Grundfos's iSOLUTIONS platform live for the first time. I built the core UI components and the data visualisation layer. I also proposed a modular frontend architecture so several teams could ship on the same platform without blocking each other.</p>"
    }
  ],
  galleri: [
    {
      title: "Home Showcase Gallery",
      gallery_key: "home-gallery",
      description: "Authentic photos of Prajwal Halwai and active software development.",
      images: [
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/profile.webp",
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-1.webp",
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-2.webp"
      ],
      captions: "Prajwal Halwai - Software Product Consultant,Hands-on software development and technical architecture"
    },
    {
      title: "About & Studio Gallery",
      gallery_key: "about-gallery",
      description: "Inside the studio: building software, writing clean architecture, and delivering for founders.",
      images: [
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-1.webp",
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/profile-1.webp",
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-2.webp"
      ],
      captions: "Engineering and product development in Odense Denmark,Prajwal Halwai Profile"
    },
    {
      title: "Services & Delivery Gallery",
      gallery_key: "services-gallery",
      description: "From discovery and technical architecture to deployment and scale.",
      images: [
        "https://assets.auxodev.dk/assets/e9ef03c8-8c42-4b2d-9ac4-e97d895bc078/working-1.webp"
      ],
      captions: "Software development process and code reviews"
    }
  ]
};

export async function fetchCollection(collectionName) {
  try {
    const res = await fetch(`${CMS_BASE}/content/${collectionName}`);
    if (!res.ok) {
      return FALLBACK_DATA[collectionName] || [];
    }
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      return data;
    }
    return FALLBACK_DATA[collectionName] || [];
  } catch {
    return FALLBACK_DATA[collectionName] || [];
  }
}

export async function fetchEntryByKey(collectionName, keyField, keyValue) {
  const all = await fetchCollection(collectionName);
  const found = all.find(item => {
    if (item[keyField] === keyValue) return true;
    if (item.slug && typeof item.slug === "object" && item.slug.en === keyValue) return true;
    if (item.slug === keyValue) return true;
    return false;
  });
  if (found) return found;
  // Try fallback
  const fallback = (FALLBACK_DATA[collectionName] || []).find(item => {
    if (item[keyField] === keyValue) return true;
    if (item.slug && typeof item.slug === "object" && item.slug.en === keyValue) return true;
    if (item.slug === keyValue) return true;
    return false;
  });
  return fallback || null;
}

export const COMPANY_BADGES = {
  "Current": {
    label: "Current",
    className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    dotClassName: "bg-emerald-400"
  },
  "E-commerce": {
    label: "E-commerce",
    className: "bg-teal-500/10 text-teal-400 border-teal-500/30",
    dotClassName: "bg-teal-400"
  },
  "Fintech": {
    label: "Fintech",
    className: "bg-sky-500/10 text-sky-400 border-sky-500/30",
    dotClassName: "bg-sky-400"
  },
  "Software development agency": {
    label: "Software development agency",
    className: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    dotClassName: "bg-purple-400"
  },
  "Water Technology": {
    label: "Water Technology",
    className: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
    dotClassName: "bg-cyan-400"
  },
  "Satellite Communications": {
    label: "Satellite Communications",
    className: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    dotClassName: "bg-amber-400"
  },
  "Autotech": {
    label: "Autotech",
    className: "bg-rose-500/10 text-rose-400 border-rose-500/30",
    dotClassName: "bg-rose-400"
  }
};

export function getCompanyBadge(item) {
  if (!item) return null;
  const cat = (item.category || "").trim();
  if (cat && COMPANY_BADGES[cat]) return COMPANY_BADGES[cat];

  const key = (item.case_key || "").toLowerCase();
  const title = (item.title || "").toLowerCase();

  if (key.includes("ongoing") || title.includes("ongoing")) return COMPANY_BADGES["Current"];
  if (key.includes("prajaya") || title.includes("prajaya") || cat === "E-commerce") return COMPANY_BADGES["E-commerce"];
  if (key.includes("fintech") || title.includes("fintech")) return COMPANY_BADGES["Fintech"];
  if (key.includes("toker") || title.includes("toker")) return COMPANY_BADGES["Software development agency"];
  if (key.includes("grundfos") || title.includes("grundfos")) return COMPANY_BADGES["Water Technology"];
  if (key.includes("quadsat") || title.includes("quadsat")) return COMPANY_BADGES["Satellite Communications"];
  if (key.includes("motor") || key.includes("dmf") || title.includes("motor")) return COMPANY_BADGES["Autotech"];

  if (cat) {
    return {
      label: cat,
      className: "bg-[#202533] text-[#CBD5E1] border-[#2A3142]",
      dotClassName: "bg-gray-400"
    };
  }
  return null;
}

